(() => {
  const BUTTON_SELECTOR = '[data-product-spec-download]';
  const LOADING_LABEL = 'Generando...';

  // Colores del PDF
  const COLORS = {
    blue: [0, 176, 255], // #00b0ff
    black: [0, 0, 0],
    white: [255, 255, 255],
    gray: [128, 128, 128]
  };

  // Dimensiones de página A4 en puntos
  const PAGE_WIDTH = 595.28;
  const PAGE_HEIGHT = 841.89;
  const MARGIN_X = 40;
  const MARGIN_Y = 40;
  const FOOTER_HEIGHT = 30;
  const LOGO_HEIGHT = 140;
  const LOGO_MAX_WIDTH = 310;
  const HEADER_HEIGHT = 158;
  const WATERMARK_OPACITY = 0.06;

  const loadImageData = (url, options = {}) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

          let watermarkDataUrl = null;
          if (options.includeWatermark) {
            const watermarkCanvas = document.createElement('canvas');
            watermarkCanvas.width = width;
            watermarkCanvas.height = height;
            const watermarkCtx = watermarkCanvas.getContext('2d');
            watermarkCtx.globalAlpha = WATERMARK_OPACITY;
            watermarkCtx.drawImage(img, 0, 0);
            watermarkDataUrl = watermarkCanvas.toDataURL('image/png');
          }

          resolve({
            dataUrl,
            width,
            height,
            watermarkDataUrl,
          });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Image load error'));

      img.src = url;
      if (img.complete) {
        img.onload();
      }
    });

  const addWatermark = (doc, logoData) => {
    if (!logoData || !logoData.watermarkDataUrl) {
      return;
    }

    const maxWidth = PAGE_WIDTH * 0.46;
    const maxHeight = PAGE_HEIGHT * 0.28;
    const scale = Math.min(maxWidth / logoData.width, maxHeight / logoData.height);
    const watermarkWidth = logoData.width * scale;
    const watermarkHeight = logoData.height * scale;
    const x = (PAGE_WIDTH - watermarkWidth) / 2;
    const y = (PAGE_HEIGHT - watermarkHeight) / 2;

    doc.addImage(logoData.watermarkDataUrl, 'PNG', x, y, watermarkWidth, watermarkHeight);
  };

  const getLogoSize = (logoData) => {
    if (!logoData) {
      return { width: 0, height: 0 };
    }

    let height = LOGO_HEIGHT;
    let width = (logoData.width / logoData.height) * height;

    if (width > LOGO_MAX_WIDTH) {
      width = LOGO_MAX_WIDTH;
      height = (logoData.height / logoData.width) * width;
    }

    return { width, height };
  };

  const addHeader = (doc, logoData) => {
    addWatermark(doc, logoData);

    if (logoData) {
      const { width, height } = getLogoSize(logoData);
      doc.addImage(logoData.dataUrl, 'JPEG', MARGIN_X, MARGIN_Y, width, height);
    }
  };

  const addFooter = (doc, contact) => {
    const footerY = PAGE_HEIGHT - FOOTER_HEIGHT;
    doc.setFontSize(11); // Tamaño aumentado
    doc.setTextColor(...COLORS.black); // Color negro
    doc.setFont('helvetica', 'normal');
    
    // Separar los elementos para poder hacerlos clickeables
    const website = contact.website || 'www.wattko.com';
    const phone = contact.phone || '';
    const email = contact.email || '';
    
    // Calcular posiciones para centrar
    const separator = '/ ';
    const websiteText = website.trim();
    const phoneText = phone.trim();
    const emailText = email.trim();
    
    // Calcular ancho total del texto (sin espacios extra al final)
    const fullText = `${websiteText}${separator}${phoneText}${separator}${emailText}`;
    const fullTextWidth = doc.getTextWidth(fullText);
    const startX = (PAGE_WIDTH - fullTextWidth) / 2;
    
    let currentX = startX;
    const linkHeight = 12; // Altura del área clickeable
    
    // Website (linkeable a wattko.com)
    const websiteUrl = website.startsWith('http') ? website : `https://${website}`;
    const websiteWidth = doc.getTextWidth(websiteText);
    doc.setTextColor(...COLORS.black);
    // Usar textWithLink si está disponible (mejor soporte para nueva ventana)
    if (doc.textWithLink) {
      try {
        doc.textWithLink(websiteText, currentX, footerY, { 
          url: websiteUrl,
          target: '_blank'
        });
      } catch (e) {
        doc.text(websiteText, currentX, footerY);
        doc.link(currentX, footerY - linkHeight, websiteWidth, linkHeight, { url: websiteUrl });
      }
    } else {
      doc.text(websiteText, currentX, footerY);
      doc.link(currentX, footerY - linkHeight, websiteWidth, linkHeight, { url: websiteUrl });
    }
    currentX += websiteWidth + doc.getTextWidth(separator);
    
    // Separador
    doc.text(separator, currentX, footerY);
    currentX += doc.getTextWidth(separator);
    
    // Teléfono (linkeable a WhatsApp)
    if (phone) {
      const phoneWidth = doc.getTextWidth(phoneText);
      const whatsappUrl = `https://wa.me/${phone.replace(/[^0-9]/g, '')}`;
      if (doc.textWithLink) {
        try {
          doc.textWithLink(phoneText, currentX, footerY, { 
            url: whatsappUrl,
            target: '_blank'
          });
        } catch (e) {
          doc.text(phoneText, currentX, footerY);
          doc.link(currentX, footerY - linkHeight, phoneWidth, linkHeight, { url: whatsappUrl });
        }
      } else {
        doc.text(phoneText, currentX, footerY);
        doc.link(currentX, footerY - linkHeight, phoneWidth, linkHeight, { url: whatsappUrl });
      }
      currentX += phoneWidth + doc.getTextWidth(separator);
      
      // Separador
      doc.text(separator, currentX, footerY);
      currentX += doc.getTextWidth(separator);
    }
    
    // Email (linkeable a Gmail compose)
    if (email) {
      const emailWidth = doc.getTextWidth(emailText);
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
      if (doc.textWithLink) {
        try {
          doc.textWithLink(emailText, currentX, footerY, { 
            url: gmailUrl,
            target: '_blank'
          });
        } catch (e) {
          doc.text(emailText, currentX, footerY);
          doc.link(currentX, footerY - linkHeight, emailWidth, linkHeight, { url: gmailUrl });
        }
      } else {
        doc.text(emailText, currentX, footerY);
        doc.link(currentX, footerY - linkHeight, emailWidth, linkHeight, { url: gmailUrl });
      }
      // No agregar espacio extra después del email (es el último elemento)
    }
  };

  const parseDescription = (description) => {
    // Crear un div temporal para parsear el HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    
    const sections = {
      description: '',
      characteristics: [],
      note: '',
      noteBeforeTable: false // Indica si la nota fue encontrada antes de la tabla
    };

    // Buscar tabla de características
    const table = tempDiv.querySelector('table');
    if (table) {
      const rows = table.querySelectorAll('tr');
      rows.forEach(row => {
        const cells = row.querySelectorAll('td, th');
        if (cells.length >= 2) {
          const characteristic = cells[0].textContent.trim();
          const value = cells[1].textContent.trim();
          if (characteristic && value) {
            sections.characteristics.push({ characteristic, value });
          }
        }
      });
    }

    // Función auxiliar para extraer nota de un texto completo
    const extractNoteFromText = (fullText) => {
      if (!fullText) return { hasNote: false, noteText: '', cleanText: fullText };
      
      let noteText = '';
      let cleanText = fullText;
      
      // Buscar patrón "Nota:" seguido de texto
      const notePattern = /Nota:\s*([^*]+?)(?=\s*\*|$)/i;
      const noteMatch = fullText.match(notePattern);
      
      // Buscar texto con asteriscos
      const asteriskPattern = /\*([^*]+)\*/;
      const asteriskMatch = fullText.match(asteriskPattern);
      
      // Construir el texto de nota combinando ambos si existen
      const noteParts = [];
      if (noteMatch) {
        noteParts.push(`Nota: ${noteMatch[1].trim()}`);
        cleanText = cleanText.replace(notePattern, '').trim();
      }
      if (asteriskMatch) {
        noteParts.push(`*${asteriskMatch[1]}*`);
        cleanText = cleanText.replace(asteriskPattern, '').trim();
      }
      
      if (noteParts.length > 0) {
        noteText = noteParts.join(' ');
      }
      
      return { 
        hasNote: !!noteText, 
        noteText, 
        cleanText: cleanText.replace(/\s+/g, ' ').trim() 
      };
    };

    // PRIMERO: Recopilar todo el texto ANTES de la tabla
    const beforeTableParts = [];
    let currentNode = tempDiv.firstChild;
    
    while (currentNode) {
      if (currentNode.nodeType === 1) { // Element node
        if (currentNode.tagName === 'TABLE' || 
            currentNode.textContent.toUpperCase().includes('CARACTERÍSTICAS')) {
          break;
        }
        if (currentNode.tagName !== 'H2' && currentNode.tagName !== 'H3') {
          const text = currentNode.textContent.trim();
          if (text && !text.toUpperCase().includes('DESCRIPCIÓN')) {
            beforeTableParts.push(text);
          }
        }
      } else if (currentNode.nodeType === 3) { // Text node
        const text = currentNode.textContent.trim();
        if (text && !text.toUpperCase().includes('DESCRIPCIÓN')) {
          beforeTableParts.push(text);
        }
      }
      currentNode = currentNode.nextSibling;
    }
    
    // Combinar todo el texto antes de la tabla y buscar nota
    const beforeTableText = beforeTableParts.join(' ').trim();
    const noteExtractionBefore = extractNoteFromText(beforeTableText);
    
    // Buscar nota DESPUÉS de la tabla también
    let noteExtractionAfter = { hasNote: false, noteText: '', cleanText: '' };
    let afterTableText = '';
    
    if (table && table.parentNode) {
      const afterTableTexts = new Set();
      
      // Obtener solo elementos hermanos después de la tabla
      let sibling = table.nextElementSibling;
      while (sibling) {
        const text = sibling.textContent.trim();
        if (text && !text.toUpperCase().includes('CARACTERÍSTICAS') && !table.contains(sibling)) {
          afterTableTexts.add(text);
        }
        sibling = sibling.nextElementSibling;
      }
      
      // Si no encontramos elementos hermanos, buscar en el contenido después de la tabla
      if (afterTableTexts.size === 0) {
        const tableIndex = tempDiv.innerHTML.indexOf(table.outerHTML);
        if (tableIndex !== -1) {
          const afterTableHTML = tempDiv.innerHTML.substring(tableIndex + table.outerHTML.length);
          const afterTableDiv = document.createElement('div');
          afterTableDiv.innerHTML = afterTableHTML;
          const text = afterTableDiv.textContent.trim();
          if (text && !text.toUpperCase().includes('CARACTERÍSTICAS')) {
            afterTableTexts.add(text);
          }
        }
      }
      
      // Combinar todo el texto después de la tabla
      afterTableText = Array.from(afterTableTexts).join(' ').trim();
      if (afterTableText) {
        noteExtractionAfter = extractNoteFromText(afterTableText);
      }
    }
    
    // Priorizar nota encontrada antes de la tabla, pero si no hay, usar la de después
    // La nota siempre se mostrará antes de la tabla en el PDF
    if (noteExtractionBefore.hasNote) {
      sections.note = noteExtractionBefore.noteText;
      sections.description = noteExtractionBefore.cleanText;
    } else if (noteExtractionAfter.hasNote) {
      sections.note = noteExtractionAfter.noteText;
      sections.description = beforeTableText; // Mantener descripción original si la nota está después
    } else {
      sections.description = beforeTableText;
    }
    
    // Siempre marcar que la nota se mostrará antes de la tabla
    sections.noteBeforeTable = true;

    return sections;
  };

  const generatePdf = async (jsPDF, data) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    let currentY = MARGIN_Y;
    const usableWidth = PAGE_WIDTH - (MARGIN_X * 2);

    // Cargar logo
    let logoData = null;
    if (data.logo) {
      try {
        logoData = await loadImageData(data.logo, { includeWatermark: true });
      } catch (error) {
        console.warn('No se pudo cargar el logo:', error);
      }
    }

    // ========== PÁGINA 1 ==========
    // Header con logo
    addHeader(doc, logoData);
    
    // Imagen principal del producto (arriba, cerca del logo, a la derecha)
    // Tamaño aumentado para que sea más visible
    const FIXED_IMAGE_HEIGHT = 170;
    const FIXED_IMAGE_WIDTH = 210;
    let imageData = null;
    let imageWidth = 0;
    let imageHeight = 0;
    let imageX = 0;
    const logoHeight = getLogoSize(logoData).height;
    const imageSpacing = 15; // Espacio entre imagen y borde derecho
    
    // Colocar la imagen arriba, lo más cerca posible del logo
    if (data.featuredImage) {
      try {
        imageData = await loadImageData(data.featuredImage);
        // Calcular escala para adaptar la imagen al espacio fijo disponible
        // El tamaño es fijo máximo (FIXED_IMAGE_HEIGHT y FIXED_IMAGE_WIDTH) pero se escala
        // proporcionalmente para mantener la relación de aspecto, por lo que el tamaño final
        // puede variar dependiendo de las dimensiones originales de la imagen
        const scale = Math.min(
          FIXED_IMAGE_WIDTH / imageData.width,
          FIXED_IMAGE_HEIGHT / imageData.height,
          1
        );
        imageWidth = imageData.width * scale;
        imageHeight = imageData.height * scale;
        imageX = PAGE_WIDTH - MARGIN_X - imageWidth - imageSpacing;
        // Colocar imagen lo más arriba posible (al mismo nivel que el logo o incluso más arriba)
        // Esto permite que la imagen esté más alta y no invada los elementos de abajo
        const imageY = MARGIN_Y; // Alineada con el margen superior (mismo nivel que el logo o más arriba)
        doc.addImage(imageData.dataUrl, 'JPEG', imageX, imageY, imageWidth, imageHeight);
      } catch (error) {
        console.warn('No se pudo cargar la imagen principal:', error);
      }
    }
    
    // Calcular el ancho disponible para el título (respetando el espacio de la imagen)
    // El título debe estar a la izquierda y no invadir el espacio de la imagen
    const imageRightEdge = imageData ? (imageX - imageSpacing) : PAGE_WIDTH - MARGIN_X;
    const titleBannerWidth = imageRightEdge - MARGIN_X - imageSpacing; // Ancho disponible menos espacio de separación
    
    // Calcular posición del título (más abajo, con buena separación del SKU)
    const titleTextPadding = 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    const titleLines = doc.splitTextToSize(data.title || 'Producto', titleBannerWidth - (titleTextPadding * 2));
    const titleLineHeight = 20;
    const titleHeight = (titleLines.length * titleLineHeight) + (titleTextPadding * 2);
    
    // Posición del título: más abajo, con buena separación del SKU (18px de separación)
    // Bajar todo el contenido para dar más espacio arriba a la imagen
    const contentStartY = MARGIN_Y + logoHeight + 30; // Bajar el contenido 30px (aumentado de 15px)
    const skuTargetY = contentStartY + titleHeight + 18; // SKU con 18px de separación del título
    const titleY = skuTargetY - titleHeight - 18; // Título 18px antes del SKU
    
    // Banner azul con título
    doc.setFillColor(...COLORS.blue);
    doc.setTextColor(...COLORS.white);
    doc.rect(MARGIN_X, titleY, titleBannerWidth, titleHeight, 'F');
    
    // Posicionar texto en el banner
    const lineBaseOffset = 15.5;
    const textStartY = titleY + titleTextPadding + lineBaseOffset;
    doc.text(titleLines, MARGIN_X + titleTextPadding, textStartY);
    
    // Actualizar currentY para el SKU (con buena separación del título)
    currentY = titleY + titleHeight + 18; // 18px después del título (buena separación)

    // SKU
    if (data.sku) {
      doc.setTextColor(...COLORS.black);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`SKU: ${data.sku}`, MARGIN_X, currentY);
      currentY += 18; // Espacio reducido
    }

    // Descripción
    currentY += 5; // Espacio reducido antes de la sección
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Descripción', MARGIN_X, currentY);
    currentY += 15; // Espacio reducido después del título

    const parsedDesc = parseDescription(data.description || '');
    
    // Texto de descripción (con paginación si es necesario)
    if (parsedDesc.description) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const descLines = doc.splitTextToSize(parsedDesc.description, usableWidth);
      const lineHeight = 14;
      
      // Agregar descripción línea por línea, verificando espacio
      descLines.forEach((line, index) => {
        // Verificar si necesitamos nueva página
        if (currentY + lineHeight > PAGE_HEIGHT - MARGIN_Y - FOOTER_HEIGHT) {
          addFooter(doc, data.contact);
          doc.addPage();
          addHeader(doc, logoData);
          currentY = MARGIN_Y + HEADER_HEIGHT + 20;
        }
        doc.text(line, MARGIN_X, currentY);
        currentY += lineHeight;
      });
      currentY += 10; // Espacio reducido después de la descripción
    }

    // Nota ANTES de la tabla (siempre se muestra antes de la tabla, independientemente de dónde esté en el HTML)
    if (parsedDesc.note) {
      currentY += 15; // Más espacio antes de la nota
      if (currentY + 20 > PAGE_HEIGHT - MARGIN_Y - FOOTER_HEIGHT) {
        addFooter(doc, data.contact);
        doc.addPage();
        addHeader(doc, logoData);
        currentY = MARGIN_Y + HEADER_HEIGHT + 20;
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      // Separar la nota del texto con asteriscos
      const noteText = parsedDesc.note.trim();
      
      // Buscar la parte que empieza con "Nota:"
      const noteMatch = noteText.match(/Nota:\s*([^*]+?)(?=\s*\*|$)/i);
      
      // Buscar el texto con asteriscos
      const asteriskMatch = noteText.match(/\*([^*]+)\*/);
      
      // Función auxiliar para renderizar texto con "Nota:" en negritas
      const renderNoteWithBold = (text, startY) => {
        let y = startY;
        const lines = doc.splitTextToSize(text, usableWidth);
        
        lines.forEach((line) => {
          // Buscar "Nota:" sin importar mayúsculas/minúsculas
          const noteRegex = /(nota:)/i;
          const noteMatch = line.match(noteRegex);
          
          if (noteMatch) {
            const noteIndex = noteMatch.index;
            const noteText = noteMatch[1]; // Preservar el texto original (puede ser "Nota:", "nota:", etc.)
            
            // Dividir la línea en partes
            const beforeNote = line.substring(0, noteIndex);
            const afterNote = line.substring(noteIndex + noteText.length);
            
            let x = MARGIN_X;
            
            // Escribir texto antes de "Nota:" si existe
            if (beforeNote.trim()) {
              doc.setFont('helvetica', 'normal');
              doc.text(beforeNote, x, y);
              x += doc.getTextWidth(beforeNote);
            }
            
            // Escribir "Nota:" en negritas (usar "Nota:" con mayúscula)
            doc.setFont('helvetica', 'bold');
            doc.text('Nota:', x, y);
            x += doc.getTextWidth('Nota:');
            
            // Escribir el resto del texto en normal
            if (afterNote.trim()) {
              doc.setFont('helvetica', 'normal');
              doc.text(afterNote, x, y);
            }
          } else {
            // Si no contiene "Nota:", escribir normalmente
            doc.setFont('helvetica', 'normal');
            doc.text(line, MARGIN_X, y);
          }
          
          y += 14; // Altura de línea
        });
        
        return y;
      };
      
      // Mostrar la nota (si existe) en un párrafo
      if (noteMatch) {
        const notePart = `Nota: ${noteMatch[1].trim()}`;
        currentY = renderNoteWithBold(notePart, currentY);
        currentY += 10; // Espacio entre párrafos
      }
      
      // Mostrar el texto con asteriscos (si existe) en un párrafo separado
      if (asteriskMatch) {
        const asteriskText = `*${asteriskMatch[1]}*`;
        const asteriskLines = doc.splitTextToSize(asteriskText, usableWidth);
        doc.setFont('helvetica', 'normal');
        doc.text(asteriskLines, MARGIN_X, currentY);
        currentY += asteriskLines.length * 14;
      }
      
      // Si no se encontró ni nota ni asteriscos, mostrar todo el texto
      if (!noteMatch && !asteriskMatch) {
        currentY = renderNoteWithBold(noteText, currentY);
      }
      
      currentY += 5; // Espacio después de las notas
    }

    // Tabla de características
    if (parsedDesc.characteristics.length > 0) {
      currentY += 5; // Espacio reducido antes de la sección
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('CARACTERÍSTICAS', MARGIN_X, currentY);
      currentY += 20; // Más espacio después del título

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      const tableStartY = currentY;
      const cellPadding = 4; // Padding vertical reducido en las celdas
      const rowHeight = 16 + (cellPadding * 2); // Altura de fila reducida con padding
      const col1Width = usableWidth * 0.4;
      const col2Width = usableWidth * 0.6;
      let rowIndex = 0;

      parsedDesc.characteristics.forEach((char, index) => {
        // Verificar si necesitamos nueva página
        if (currentY + rowHeight > PAGE_HEIGHT - MARGIN_Y - FOOTER_HEIGHT) {
          addFooter(doc, data.contact);
          doc.addPage();
          addHeader(doc, logoData);
          currentY = MARGIN_Y + HEADER_HEIGHT + 20;
        }

        const rowY = currentY;
        const isEven = rowIndex % 2 === 0;
        
        // Fondo alternado (ajustado para incluir padding)
        if (isEven) {
          doc.setFillColor(245, 245, 245);
          doc.rect(MARGIN_X, rowY, usableWidth, rowHeight, 'F');
        }

        // Texto de característica (con padding vertical)
        doc.setTextColor(...COLORS.black);
        doc.setFont('helvetica', 'bold');
        const charLines = doc.splitTextToSize(char.characteristic, col1Width - 20);
        const charTextY = rowY + cellPadding + 11; // Padding arriba + ajuste de línea base
        doc.text(charLines, MARGIN_X + 10, charTextY);
        
        // Texto de valor (con padding vertical)
        doc.setFont('helvetica', 'normal');
        const valueLines = doc.splitTextToSize(char.value, col2Width - 20);
        const valueTextY = rowY + cellPadding + 11; // Padding arriba + ajuste de línea base
        doc.text(valueLines, MARGIN_X + col1Width + 10, valueTextY);
        
        // Calcular altura de la fila basada en el contenido
        const contentHeight = Math.max(charLines.length, valueLines.length) * 13;
        currentY += Math.max(rowHeight, contentHeight + (cellPadding * 2));
        rowIndex++;
      });
      
      // Agregar espacio después de la tabla
      currentY += 15;
    }

    // Marca
    if (data.vendor) {
      currentY += 5; // Espacio antes de la marca
      if (currentY + 20 > PAGE_HEIGHT - MARGIN_Y - FOOTER_HEIGHT) {
        addFooter(doc, data.contact);
        doc.addPage();
        addHeader(doc, logoData);
        currentY = MARGIN_Y + HEADER_HEIGHT + 20;
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`Marca: ${data.vendor}`, MARGIN_X, currentY);
    }

    // Footer página 1
    addFooter(doc, data.contact);

    // ========== PÁGINA DE FOTOS (Primeras 4 imágenes en cuadrícula 2x2) ==========
    // Incluir todas las imágenes disponibles
    let galleryImages = [];
    
    // Si hay array de imágenes, usarlo
    if (data.images && Array.isArray(data.images) && data.images.length > 0) {
      galleryImages = [...data.images];
      
      // Intentar excluir la imagen principal si está duplicada
      if (data.featuredImage) {
        const normalizeUrl = (url) => {
          if (!url) return '';
          try {
            // Remover parámetros de consulta y obtener la base de la URL
            let base = url.split('?')[0];
            // Remover dimensiones al final (ej: _1200x1200.jpg)
            base = base.replace(/_\d+x\d+\.(jpg|jpeg|png|webp)$/i, '');
            // Obtener el ID o nombre del archivo para comparar
            const parts = base.split('/');
            const filename = parts[parts.length - 1];
            // Extraer ID numérico si existe (ej: "1234567890.jpg" -> "1234567890")
            const match = filename.match(/(\d+)/);
            return match ? match[1] : filename.split('.')[0];
          } catch (e) {
            return url;
          }
        };
        
        const featuredBase = normalizeUrl(data.featuredImage);
        
        const featuredIndex = galleryImages.findIndex(img => {
          const imgBase = normalizeUrl(img);
          return imgBase === featuredBase || img.includes(featuredBase) || featuredBase.includes(imgBase);
        });
        
        if (featuredIndex !== -1) {
          galleryImages.splice(featuredIndex, 1);
        }
      }
    } else {
      // Intentar obtener imágenes del DOM como fallback
      try {
        const productImages = document.querySelectorAll('.yv-product-slider-item img, .product-gallery img');
        if (productImages && productImages.length > 0) {
          productImages.forEach((img) => {
            if (img.src && !img.src.includes('data:')) {
              // Obtener URL de alta resolución
              let imgSrc = img.src;
              if (img.srcset) {
                const srcsetParts = img.srcset.split(', ');
                if (srcsetParts.length > 0) {
                  imgSrc = srcsetParts[srcsetParts.length - 1].split(' ')[0];
                }
              }
              // Remover parámetros de tamaño y obtener URL base
              imgSrc = imgSrc.replace(/[?&]width=\d+/g, '').replace(/[?&]height=\d+/g, '');
              if (!galleryImages.includes(imgSrc)) {
                galleryImages.push(imgSrc);
              }
            }
          });
        }
      } catch (e) {
        // Silenciar error
      }
    }
    
    // Tomar solo las primeras 4 imágenes
    const photosToShow = galleryImages.slice(0, 4);
    
    if (photosToShow.length > 0) {
      doc.addPage();
      addHeader(doc, logoData);
      
      // Calcular espacio disponible para las imágenes (después del header y antes del footer)
      const footerTop = PAGE_HEIGHT - FOOTER_HEIGHT;
      const availableHeight = footerTop - (MARGIN_Y + HEADER_HEIGHT) - 40; // 40px de margen total
      const availableWidth = usableWidth;
      
      // Calcular dimensiones para cuadrícula 2x2 centrada (imágenes más grandes)
      const imageSpacing = 8; // Espacio entre imágenes
      const gridWidth = Math.min(availableWidth * 0.95, 480); // Ancho aumentado (95% del ancho disponible o 480px)
      const gridHeight = Math.min(availableHeight * 0.95, 480); // Alto aumentado (95% del alto disponible o 480px)
      
      // Centrar el grupo de imágenes horizontalmente
      const gridStartX = MARGIN_X + (availableWidth - gridWidth) / 2;
      const gridStartY = MARGIN_Y + HEADER_HEIGHT + 20 + (availableHeight - gridHeight) / 2;
      
      // Título "Fotos" centrado y más cerca de las imágenes
      const titleY = gridStartY - 25; // 25px arriba del grupo de imágenes
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...COLORS.black);
      const titleWidth = doc.getTextWidth('Fotos');
      const titleX = MARGIN_X + (availableWidth - titleWidth) / 2; // Centrar horizontalmente
      doc.text('Fotos', titleX, titleY);
      
      const imageWidth = (gridWidth - imageSpacing) / 2;
      const imageHeight = (gridHeight - imageSpacing) / 2;
      
      // Posiciones para las 4 imágenes (centradas)
      const positions = [
        { x: gridStartX, y: gridStartY }, // Esquina superior izquierda
        { x: gridStartX + imageWidth + imageSpacing, y: gridStartY }, // Esquina superior derecha
        { x: gridStartX, y: gridStartY + imageHeight + imageSpacing }, // Esquina inferior izquierda
        { x: gridStartX + imageWidth + imageSpacing, y: gridStartY + imageHeight + imageSpacing } // Esquina inferior derecha
      ];
      
      // Cargar y agregar las 4 imágenes
      for (let i = 0; i < photosToShow.length && i < 4; i++) {
        try {
          const imgData = await loadImageData(photosToShow[i]);
          const pos = positions[i];
          
          // Calcular escala para que la imagen se adapte al espacio disponible manteniendo proporción
          const scale = Math.min(
            imageWidth / imgData.width,
            imageHeight / imgData.height,
            1
          );
          const scaledWidth = imgData.width * scale;
          const scaledHeight = imgData.height * scale;
          
          // Centrar la imagen en su celda
          const offsetX = (imageWidth - scaledWidth) / 2;
          const offsetY = (imageHeight - scaledHeight) / 2;
          
          doc.addImage(
            imgData.dataUrl, 
            'JPEG', 
            pos.x + offsetX, 
            pos.y + offsetY, 
            scaledWidth, 
            scaledHeight
          );
        } catch (error) {
          // Silenciar error
        }
      }
      
      // Footer página de fotos
      addFooter(doc, data.contact);
    }

    // Guardar PDF
    const slugify = (value) =>
      (value || 'producto')
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    const fileName = `ficha-tecnica-${slugify(data.title)}.pdf`;
    doc.save(fileName);
    return fileName;
  };

  const trackSpecSheetDownload = (data, fileName) => {
    const payload = {
      event: 'spec_sheet_download',
      product_id: data.id || '',
      product_handle: data.handle || '',
      product_title: data.title || '',
      product_sku: data.sku || '',
      product_variant_id: data.variantId || '',
      product_vendor: data.vendor || '',
      file_name: fileName || '',
    };

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'spec_sheet_download', {
          product_id: payload.product_id,
          product_handle: payload.product_handle,
          product_title: payload.product_title,
          product_sku: payload.product_sku,
          product_variant_id: payload.product_variant_id,
          product_vendor: payload.product_vendor,
          file_name: payload.file_name,
        });
      }

      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('spec_sheet_download', {
          productId: payload.product_id,
          handle: payload.product_handle,
          productTitle: payload.product_title,
          sku: payload.product_sku,
          variantId: payload.product_variant_id,
          vendor: payload.product_vendor,
          fileName: payload.file_name,
        });
      }
    } catch (error) {
      console.warn('No se pudo registrar la descarga de la ficha técnica.', error);
    }
  };

  const handleClick = async (event) => {
    const button = event.currentTarget;
    const { jsPDF } = window.jspdf || {};

    if (!jsPDF) {
      console.warn('jsPDF no está disponible.');
      return;
    }

    const data = window.productSpecSheetData;
    if (!data) {
      console.warn('Los datos del producto para la ficha técnica no están disponibles.');
      return;
    }

    const skuEl = document.querySelector('[data-product-sku]');
    if (skuEl) {
      const liveSku = skuEl.textContent.trim();
      if (liveSku) {
        data.sku = liveSku;
      }
    }

    // Obtener el logo del DOM si no está disponible
    if (!data.logo) {
      const logoImg = document.querySelector('.site-logo-image, .header__heading-logo, .Yuva-logo');
      if (logoImg && logoImg.src) {
        const logoSrc = logoImg.srcset ? logoImg.srcset.split(', ').pop().split(' ')[0] : logoImg.src;
        data.logo = logoSrc.replace(/width=\d+/, 'width=300');
      }
    }

    const textSpan = button.querySelector('.button-text');
    const originalLabel = button.dataset.originalLabel || (textSpan ? textSpan.textContent.trim() : button.textContent.trim());
    button.dataset.originalLabel = originalLabel;
    button.disabled = true;
    
    if (textSpan) {
      textSpan.textContent = LOADING_LABEL;
    } else {
      button.textContent = LOADING_LABEL;
    }

    try {
      const fileName = await generatePdf(jsPDF, data);
      trackSpecSheetDownload(data, fileName);
    } catch (error) {
      console.error('Error al generar la ficha técnica.', error);
      alert('No se pudo generar la ficha técnica. Intenta nuevamente más tarde.');
    } finally {
      button.disabled = false;
      if (textSpan) {
        textSpan.textContent = originalLabel;
      } else {
        button.textContent = originalLabel;
      }
    }
  };

  const init = () => {
    const button = document.querySelector(BUTTON_SELECTOR);
    if (!button) {
      return;
    }

    button.addEventListener('click', handleClick);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
